/**
 * Progress Panel Component
 * Displays real-time progress updates for RIDER and Husarz agents.
 *
 * Features:
 * - Wave animation on progress bar
 * - Real-time elapsed timer (count-up)
 * - ETA countdown from backend
 * - 320-char status description
 * - Dynamic task list with checkmarks
 *
 * NOTE: This component is ONLY for RIDER and Husarz agents.
 * Agent 128 does NOT have progress updates.
 */
import { useMemo, useState, useEffect } from 'react';
import { AgentType, AGENT_TYPE_INFO, hasProgressObserver } from '../types/task';
import { useProgressWebSocket } from '../hooks/useProgressWebSocket';

interface ProgressPanelProps {
  taskId: number;
  agentType: AgentType;
  taskStatus: string;
}

export default function ProgressPanel({ taskId, agentType, taskStatus }: ProgressPanelProps) {
  // Only show for RIDER and Husarz agents during processing
  const shouldShow = hasProgressObserver(agentType) && taskStatus?.toLowerCase() === 'processing';

  const { status, isConnected, error } = useProgressWebSocket({
    taskId,
    enabled: shouldShow
  });

  // Local elapsed timer that updates every second
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [startTime] = useState(() => Date.now());

  useEffect(() => {
    if (!shouldShow) return;

    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [shouldShow, startTime]);

  // Format elapsed time
  const formatElapsed = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins < 60) return `${mins}m ${secs}s`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m`;
  };

  // Calculate completed and total tasks
  const taskStats = useMemo(() => {
    if (!status?.task_list) return { completed: 0, total: 0 };
    const completed = status.task_list.filter(t => t.completed).length;
    return { completed, total: status.task_list.length };
  }, [status?.task_list]);

  // Don't render if not applicable
  if (!shouldShow) {
    return null;
  }

  // Show connecting state
  if (!isConnected && !status) {
    return (
      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
          <span className="text-sm text-gray-600">Connecting to {AGENT_TYPE_INFO[agentType].name} progress...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !status) {
    return (
      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-yellow-600">&#9888;</span>
          <span className="text-sm text-yellow-700">Progress updates unavailable: {error}</span>
        </div>
      </div>
    );
  }

  // Show "waiting for data" state when connected but no status yet
  if (!status) {
    const agentInfo = AGENT_TYPE_INFO[agentType];
    return (
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center gap-3">
          <div className="animate-pulse w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center">
            <span className="text-blue-500">🔄</span>
          </div>
          <div>
            <span className="text-sm font-medium text-blue-700">{agentInfo.name} starting...</span>
            <p className="text-xs text-blue-500">Waiting for first progress update</p>
          </div>
        </div>
      </div>
    );
  }

  const agentInfo = AGENT_TYPE_INFO[agentType];
  const isRider = agentType === AgentType.RIDER;
  const isHusarz = agentType === AgentType.HUSARZ;
  const progressPercent = status.progress_percent || 0;

  return (
    <div className={`mb-6 p-5 rounded-xl border-2 shadow-sm ${
      isRider
        ? 'bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200'
        : 'bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200'
    }`}>
      {/* Header Row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isRider ? 'bg-blue-500' : 'bg-amber-500'
          }`}>
            <span className="text-xl text-white">{isRider ? '🔄' : '🛡️'}</span>
          </div>
          <div>
            <h3 className={`font-semibold text-lg ${isRider ? 'text-blue-900' : 'text-amber-900'}`}>
              {agentInfo.name} Progress
            </h3>
            <div className="flex items-center gap-2 text-xs">
              {isConnected && (
                <span className="flex items-center gap-1 text-green-600">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  Live
                </span>
              )}
              <span className="text-gray-500">|</span>
              <span className={`font-mono ${isRider ? 'text-blue-600' : 'text-amber-600'}`}>
                Elapsed: {formatElapsed(elapsedSeconds)}
              </span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-bold ${isRider ? 'text-blue-600' : 'text-amber-600'}`}>
            {progressPercent}%
          </div>
          {status.progress_eta && (
            <div className={`text-sm font-medium ${isRider ? 'text-blue-500' : 'text-amber-500'}`}>
              {status.progress_eta}
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar with Wave Animation */}
      <div className="relative h-4 bg-white/80 rounded-full overflow-hidden mb-4 shadow-inner">
        {/* Background track */}
        <div className={`absolute inset-0 ${isRider ? 'bg-blue-100' : 'bg-amber-100'}`} />

        {/* Progress fill with wave animation */}
        <div
          className={`absolute left-0 top-0 h-full transition-all duration-700 ease-out rounded-full ${
            isRider ? 'bg-gradient-to-r from-blue-400 to-blue-600' : 'bg-gradient-to-r from-amber-400 to-amber-600'
          }`}
          style={{ width: `${progressPercent}%` }}
        >
          {/* Wave animation overlay */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{
              background: `linear-gradient(90deg,
                transparent 0%,
                rgba(255,255,255,0.3) 25%,
                rgba(255,255,255,0.5) 50%,
                rgba(255,255,255,0.3) 75%,
                transparent 100%)`,
              animation: 'wave 2s linear infinite',
              backgroundSize: '200% 100%'
            }}
          />
        </div>

        {/* Shimmer effect */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
            backgroundSize: '200% 100%'
          }}
        />
      </div>

      {/* Status Description - Prominent Display */}
      {status.status_description && (
        <div className={`p-3 rounded-lg mb-4 ${
          isRider ? 'bg-blue-100/70' : 'bg-amber-100/70'
        }`}>
          <div className="flex items-start gap-2">
            <span className={`text-lg ${isRider ? 'text-blue-500' : 'text-amber-500'}`}>
              &#9432;
            </span>
            <p className={`text-sm leading-relaxed ${isRider ? 'text-blue-800' : 'text-amber-800'}`}>
              {status.status_description}
            </p>
          </div>
        </div>
      )}

      {/* Task List */}
      {status.task_list && status.task_list.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className={`text-sm font-semibold ${isRider ? 'text-blue-700' : 'text-amber-700'}`}>
              Tasks ({taskStats.completed}/{taskStats.total})
            </span>
            <div className={`text-xs px-2 py-1 rounded-full ${
              isRider ? 'bg-blue-200 text-blue-700' : 'bg-amber-200 text-amber-700'
            }`}>
              {taskStats.total - taskStats.completed} remaining
            </div>
          </div>
          <ul className="space-y-1.5">
            {status.task_list.map((task, index) => (
              <li
                key={index}
                className={`flex items-center gap-2 py-1 px-2 rounded transition-all duration-300 ${
                  task.completed
                    ? 'bg-green-50 text-gray-500'
                    : isRider ? 'bg-blue-50/50 text-blue-800' : 'bg-amber-50/50 text-amber-800'
                }`}
              >
                {task.completed ? (
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                    isRider ? 'border-blue-300 bg-blue-50' : 'border-amber-300 bg-amber-50'
                  }`}>
                    <div className={`w-2 h-2 rounded-full animate-pulse ${
                      isRider ? 'bg-blue-400' : 'bg-amber-400'
                    }`} />
                  </div>
                )}
                <span className={`text-sm ${task.completed ? 'line-through' : 'font-medium'}`}>
                  {task.content}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Husarz-specific: Horse/Rider Status */}
      {isHusarz && (status as any).horse_status && (
        <div className="mt-4 pt-4 border-t border-amber-200">
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-lg">🐴</span>
              <span className="text-amber-700">Horse:</span>
              <span className={`font-semibold px-2 py-0.5 rounded ${
                (status as any).horse_status === 'completed' ? 'bg-green-100 text-green-700' :
                (status as any).horse_status === 'running' ? 'bg-blue-100 text-blue-700' :
                (status as any).horse_status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {(status as any).horse_status}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg">🏇</span>
              <span className="text-amber-700">Rider:</span>
              <span className={`font-semibold px-2 py-0.5 rounded ${
                (status as any).rider_status === 'approved' ? 'bg-green-100 text-green-700' :
                (status as any).rider_status === 'verifying' ? 'bg-blue-100 text-blue-700' :
                (status as any).rider_status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {(status as any).rider_status}
              </span>
            </div>
            {(status as any).verification_confidence !== null && (
              <div className="flex items-center gap-2">
                <span className="text-amber-700">Confidence:</span>
                <span className="font-bold text-amber-600">
                  {((status as any).verification_confidence * 100).toFixed(0)}%
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes wave {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}
